import { useEffect, useRef, useState, useMemo } from 'react';

/**
 * Custom hook for intersection observer with viewport bug fix
 * @param {Object} options - Configuration options
 * @param {string} options.rootMargin - Margin around the root (e.g., "50px")
 * @param {number} options.threshold - Threshold for intersection (0-1)
 * @param {boolean} options.once - Whether to observe only once
 * @returns {Object} { elementRef, isIntersecting, hasIntersected }
 */
const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const elementRef = useRef(null);

  // Memoize options to prevent unnecessary effect re-runs
  const memoizedOptions = useMemo(() => ({
    threshold: options.threshold || 0,
    rootMargin: options.rootMargin || '0px',
    once: options.once
  }), [options.threshold, options.rootMargin, options.once]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || window.isSSR) return;

    // Check if element is already in viewport on mount (fixes the react-lazyload bug)
    const rect = element.getBoundingClientRect();
    const isAlreadyVisible = (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );

    if (isAlreadyVisible) {
      setIsIntersecting(true);
      setHasIntersected(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      const isVisible = entry.isIntersecting;
      setIsIntersecting(isVisible);

      if (isVisible) {
        setHasIntersected(true);
        if (memoizedOptions.once) {
          observer.unobserve(element);
        }
      }
    }, {
      threshold: memoizedOptions.threshold,
      rootMargin: memoizedOptions.rootMargin,
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [memoizedOptions]);

  return { elementRef, isIntersecting, hasIntersected };
};

export default useIntersectionObserver;
